import React, { useState, useEffect } from "react";
import { Building2, Users, Truck, Shield, Loader2, Trash2, UserCheck, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ACCENT = "#FF4D00";
const API    = process.env.REACT_APP_BACKEND_URL;

const PROFILE_TABS = [
  { key: "carriers",   label: "Carriers / Fleet", color: "#3B82F6", icon: Building2 },
  { key: "drivers",    label: "Drivers",           color: "#FF4D00", icon: Truck },
  { key: "recruiters", label: "Recruiters",        color: "#10B981", icon: Users },
  { key: "setters",    label: "Setters",           color: "#A855F7", icon: UserCheck },
  { key: "safety",     label: "Safety",            color: "#F59E0B", icon: Shield },
];

function CarrierLogoThumb({ carrierKey, name }) {
  const [broken, setBroken] = useState(false);
  const logoUrl = `${API}/api/market/carrier/logo/${encodeURIComponent(carrierKey)}`;
  const abbr    = (name || "").split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");
  if (broken) return (
    <div className="w-8 h-8 shrink-0 flex items-center justify-center font-head text-[10px] font-black"
      style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.25)", color: "#3B82F6" }}>
      {abbr || "?"}
    </div>
  );
  return (
    <img src={logoUrl} alt={name} className="w-8 h-8 shrink-0 object-contain"
      style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
      onError={() => setBroken(true)} />
  );
}

function timeAgo(isoStr) {
  if (!isoStr) return "never";
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/** Inline confirm — prevents accidental deletes without window.confirm */
function DeleteButton({ id, label, onConfirm, isDeleting }) {
  const [confirming, setConfirming] = useState(false);
  if (isDeleting) return <Loader2 className="w-4 h-4 animate-spin" style={{ color: "rgba(255,255,255,0.3)" }} />;
  if (confirming) return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] font-head uppercase tracking-[0.1em] text-red-400">Sure?</span>
      <button onClick={() => { setConfirming(false); onConfirm(); }}
        data-testid={`confirm-delete-${id}`}
        className="px-2 py-0.5 text-[10px] font-head uppercase tracking-[0.1em] font-bold"
        style={{ background: "rgba(239,68,68,0.15)", color: "#EF4444", border: "1px solid rgba(239,68,68,0.3)" }}>
        Yes
      </button>
      <button onClick={() => setConfirming(false)}
        className="px-2 py-0.5 text-[10px] font-head uppercase tracking-[0.1em]"
        style={{ background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.4)", border: "1px solid rgba(255,255,255,0.1)" }}>
        No
      </button>
    </div>
  );
  return (
    <button onClick={() => setConfirming(true)}
      data-testid={`delete-${id}`}
      title={`Remove ${label}`}
      className="p-1.5 transition-colors hover:text-red-400"
      style={{ color: "rgba(255,255,255,0.2)" }}>
      <Trash2 className="w-3.5 h-3.5" />
    </button>
  );
}

export function ProfilesPanel({ token, onRefresh }) {
  const [data, setData]         = useState({ carriers: [], drivers: [], recruiters: [], setters: [], safety: [] });
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState("carriers");
  const [deleting, setDeleting] = useState(null);

  async function load() {
    const tok = token
      || localStorage.getItem("hylas_market_token")
      || sessionStorage.getItem("hylas_market_token");
    if (!tok) return;
    setLoading(true);
    try {
      const r = await api.marketAdminListAllProfiles(tok);
      setData(r);
    } catch (err) {
      toast.error("Failed to load profiles");
    }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function deleteDriver(appId, name) {
    setDeleting(appId);
    try {
      await api.marketAdminDeleteDriver(token, appId);
      toast.success(`${name} removed`);
      setData(p => ({ ...p, drivers: p.drivers.filter(d => d.app_id !== appId) }));
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
    finally { setDeleting(null); }
  }

  async function deleteCarrier(carrierKey, name) {
    setDeleting(carrierKey);
    try {
      await api.marketAdminDeleteCarrier(token, carrierKey);
      toast.success(`${name} removed`);
      setData(p => ({ ...p, carriers: p.carriers.filter(c => c.carrier_key !== carrierKey) }));
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
    finally { setDeleting(null); }
  }

  async function deleteHubUser(hub, userId, name) {
    setDeleting(userId);
    try {
      await api.marketAdminDeleteHubUser(token, hub, userId);
      toast.success(`${name} removed from ${hub}`);
      const stateKey = hub === "safety" ? "safety" : hub + "s";
      setData(p => ({ ...p, [stateKey]: (p[stateKey] || []).filter(u => u.user_id !== userId) }));
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
    finally { setDeleting(null); }
  }

  const activeTab = PROFILE_TABS.find((t) => t.key === tab);
  const rows = data[tab] || [];

  return (
    <div className="space-y-4">
      <div>
        <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-0.5">Admin</p>
        <h3 className="font-head text-xl font-black uppercase tracking-[0.04em]" style={{ color: ACCENT }}>Manage Profiles</h3>
        <p className="text-xs text-white/30 mt-0.5">Remove carriers, drivers, recruiters, setters, or safety users from the platform.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 flex-wrap">
        {PROFILE_TABS.map((t) => {
          const Icon = t.icon;
          const count = (data[t.key] || []).length;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`profiles-tab-${t.key}`}
              className="flex-1 min-w-[70px] flex flex-col items-center gap-0.5 py-2 px-1 font-head text-[9px] uppercase tracking-[0.1em] transition-all"
              style={{
                background: tab === t.key ? `${t.color}18` : "rgba(255,255,255,0.03)",
                border: `1px solid ${tab === t.key ? `${t.color}40` : "rgba(255,255,255,0.07)"}`,
                color: tab === t.key ? t.color : "rgba(255,255,255,0.35)",
              }}>
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              <span className="font-black">{count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: ACCENT }} />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10" style={{ border: "1px dashed rgba(255,255,255,0.08)" }}>
          <AlertTriangle className="w-4 h-4" style={{ color: "rgba(255,255,255,0.15)" }} />
          <p className="text-xs text-white/25 font-head uppercase tracking-[0.2em]">No {activeTab?.label} found</p>
        </div>
      ) : (
        <div className="space-y-1.5" data-testid={`profiles-list-${tab}`}>

          {/* DRIVERS */}
          {tab === "drivers" && rows.map((d) => (
            <div key={d.app_id} className="flex items-center gap-3 px-4 py-3"
              style={{ background: "#161618", border: "1px solid rgba(255,255,255,0.06)" }}>
              <div className="w-8 h-8 shrink-0 flex items-center justify-center font-head text-[10px] font-black"
                style={{ background: "rgba(255,77,0,0.1)", border: "1px solid rgba(255,77,0,0.2)", color: ACCENT }}>
                {(d.name[0] || "?").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-head text-sm font-bold text-white truncate">{d.name}</p>
                <p className="text-[10px] text-white/35 truncate">
                  {[d.cdl_class, d.state, d.public_slug].filter(Boolean).join(" · ")}
                </p>
              </div>
              <DeleteButton id={d.app_id} label={d.name} onConfirm={() => deleteDriver(d.app_id, d.name)} isDeleting={deleting === d.app_id} />
            </div>
          ))}

          {/* CARRIERS */}
          {tab === "carriers" && rows.map((c) => (
            <div key={c.carrier_key} className="flex items-center gap-3 px-4 py-3"
              style={{ background: "#161618", border: "1px solid rgba(255,255,255,0.06)" }}>
              <CarrierLogoThumb carrierKey={c.carrier_key} name={c.name} />
              <div className="flex-1 min-w-0">
                <p className="font-head text-sm font-bold text-white truncate">{c.name}</p>
                <p className="text-[10px] text-white/35 truncate">
                  {c.email || "no email"}{c.mc_dot ? ` · MC/DOT: ${c.mc_dot}` : ""}
                </p>
              </div>
              <DeleteButton id={c.carrier_key} label={c.name} onConfirm={() => deleteCarrier(c.carrier_key, c.name)} isDeleting={deleting === c.carrier_key} />
            </div>
          ))}

          {/* RECRUITERS / SETTERS / SAFETY — shared layout */}
          {["recruiters", "setters", "safety"].includes(tab) && rows.map((u) => (
            <div key={u.user_id} className="flex items-center gap-3 px-4 py-3"
              style={{ background: "#161618", border: "1px solid rgba(255,255,255,0.06)" }}>
              <div className="w-8 h-8 shrink-0 flex items-center justify-center font-head text-[10px] font-black"
                style={{ background: `${activeTab?.color}15`, border: `1px solid ${activeTab?.color}30`, color: activeTab?.color }}>
                {(u.name?.[0] || u.email?.[0] || "?").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-head text-sm font-bold text-white truncate">{u.name || "Unknown"}</p>
                <p className="text-[10px] text-white/35 truncate">
                  {u.email || "hub credentials"} · {timeAgo(u.last_login)}
                </p>
              </div>
              <DeleteButton id={u.user_id} label={u.name || u.email} onConfirm={() => deleteHubUser(u.hub, u.user_id, u.name || u.email)} isDeleting={deleting === u.user_id} />
            </div>
          ))}

        </div>
      )}
    </div>
  );
}
